import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import {
  Address,
  Associate,
  Note,
  ServiceAppointment as Appointment,
  Vehicle,
  WorkOrder,
  AlternateTransportation,
} from '@quorum/models/xs-resource';
import {
  appointmentCaseAdapter,
  AppointmentCasesEntityActionTypes,
  initialQuoteLinesState,
  initialSelectedAppointmentCaseState,
  initialServiceAppointmentDeclinedOperationsState,
  initialServiceGroupsState,
  initialWorkOrderState,
  quoteLinesAdapter,
  QuoteLinesEntityActionTypes,
  QuoteLinesState,
  SelectedAppointmentCasesState,
  serviceAppointmentDeclinedOperationsAdapter,
  ServiceAppointmentDeclinedOperationsEntityActionTypes,
  ServiceAppointmentDeclinedOperationsState,
  serviceGroupsAdapter,
  ServiceGroupsEntityActionTypes,
  ServiceGroupsState,
} from '@quorum/sha-entities/fixed-operations';
import { ServiceLaneActionTypes } from './service-lane.actions';

export const SERVICELANE_FEATURE_KEY = 'serviceLane';

//#region open appointments
export interface OpenAppointmentState extends EntityState<Appointment> {
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string;
}

export const appointmentAdapter: EntityAdapter<Appointment> = createEntityAdapter<Appointment>({});
export const initialOpenAppointmentState: OpenAppointmentState = appointmentAdapter.getInitialState({
  status: 'idle',
  error: null,
});
//#endregion

//#region unacknowledgedAppointments
export interface UnacknowledgedAppointmentsState extends EntityState<Appointment> {
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string;
}

export const initialUnacknolwedgedAppointmentsState: UnacknowledgedAppointmentsState =
  appointmentAdapter.getInitialState({
    status: 'idle',
    error: null,
  });
//#endregion

//#region todays appointments
export interface TodaysAppointmentsState extends EntityState<Appointment> {
  loading: boolean;
}

export const initialtodaysAppointmentState: TodaysAppointmentsState = appointmentAdapter.getInitialState({
  loading: true,
});

export interface TodaysWorkOrderAppointmentsState extends EntityState<WorkOrder> {}
export const workOrderAppointmentAdapter: EntityAdapter<WorkOrder> = createEntityAdapter<WorkOrder>({});
export const initialWoAppointmentState: TodaysWorkOrderAppointmentsState = workOrderAppointmentAdapter.getInitialState(
  {}
);
//#endregion

//#region service history
export interface ServiceHistoryState extends EntityState<WorkOrder> {}
export const initialServiceHistoryState: ServiceHistoryState = workOrderAppointmentAdapter.getInitialState({});
//#endregion

//#region ui
export interface UiState {
  [key: string]: any;
  verifyCustomerCreateWOEnabled: boolean;
  workOrderRequiredFieldsPresent: { odometerIn: boolean; serviceAdvisorId: boolean; serviceCode: boolean };
  unsavedLineItem: boolean;
  mediaUploading?: boolean;
}
export const initialUiState: any = {
  verifyCustomerCreateWOEnabled: true,
  workOrderRequiredFieldsPresent: { odometerIn: false, serviceAdvisorId: false, serviceCode: false },
  unsavedLineItem: false,
  mediaUploading: false,
};
//#endregion

//#region appointmentFilters
export interface AppointmentFiltersState {
  [key: string]: any;
  appointmentDateFrom: Date;
  appointmentDateTo: Date;
  myAppointmentsToggle: boolean;
  showUnacknowledgedToggle: boolean;
  serviceCode: string;
  transportOptionId: number;
}
export const initialAppointmentFiltersState: any = {
  appointmentDateFrom: new Date(),
  appointmentDateTo: new Date(),
  myAppointmentsToggle: false,
  showUnacknowledgedToggle: false,
  serviceCode: null,
  transportOptionId: null,
};
//#endregion

//#region alternateTransportations
export interface AlternateTransportationsState extends EntityState<AlternateTransportation> {}

export const alternateTransportationsAdapter: EntityAdapter<AlternateTransportation> =
  createEntityAdapter<AlternateTransportation>({});

export const initialAlternateTransportationsState: AlternateTransportationsState =
  alternateTransportationsAdapter.getInitialState({});
//#end region

export interface ServiceLaneState {
  alternateTransportations: AlternateTransportationsState;
  appointmentFilters: AppointmentFiltersState;
  approvedQuoteLines: QuoteLinesState;
  customerAddress: Address;
  customerAssociate: Associate;
  customerNotes: Note;
  appointmentNotes: Note;
  vehicleNotes: Note;
  declinedQuoteLines: QuoteLinesState;
  openAppointments: OpenAppointmentState;
  quoteLines: QuoteLinesState;
  selectedAppointment: Appointment;
  selectedAppointmentCases: SelectedAppointmentCasesState;
  serviceAppointmentDeclinedOperations: ServiceAppointmentDeclinedOperationsState;
  serviceGroups: ServiceGroupsState;
  serviceHistory: ServiceHistoryState;
  todaysAppointments: TodaysAppointmentsState;
  todaysWorkOrderAppointments: TodaysWorkOrderAppointmentsState;
  ui: UiState;
  unacknowledgedAppointments: UnacknowledgedAppointmentsState;
  vehicle: Vehicle;
}

export interface ServiceLanePartialState {
  readonly [SERVICELANE_FEATURE_KEY]: ServiceLaneState;
}

export const initialState: ServiceLaneState = {
  alternateTransportations: initialAlternateTransportationsState,
  appointmentFilters: initialAppointmentFiltersState,
  approvedQuoteLines: initialQuoteLinesState,
  customerAddress: null,
  customerAssociate: null,
  customerNotes: null,
  appointmentNotes: null,
  vehicleNotes: null,
  declinedQuoteLines: initialQuoteLinesState,
  openAppointments: initialOpenAppointmentState,
  quoteLines: initialQuoteLinesState,
  selectedAppointment: null,
  selectedAppointmentCases: initialSelectedAppointmentCaseState,
  serviceAppointmentDeclinedOperations: initialServiceAppointmentDeclinedOperationsState,
  serviceGroups: initialServiceGroupsState,
  serviceHistory: initialServiceHistoryState,
  todaysAppointments: initialtodaysAppointmentState,
  todaysWorkOrderAppointments: initialWoAppointmentState,
  ui: initialUiState,
  unacknowledgedAppointments: initialUnacknolwedgedAppointmentsState,
  vehicle: null,
};

export function serviceLaneReducer(state: ServiceLaneState = initialState, action: any): ServiceLaneState {
  switch (action.type) {
    case ServiceLaneActionTypes.GetCustomerAssociateFromServerSuccess: {
      state = { ...state, customerAssociate: action.payload.associate, customerAddress: action.payload.address };
      break;
    }
    case ServiceLaneActionTypes.UpdateCustomerAssociateOnServerSuccess: {
      state = { ...state, customerAssociate: action.payload };
      break;
    }
    case ServiceLaneActionTypes.UpdateCustomerAddressOnServerSuccess: {
      state = { ...state, customerAddress: action.payload };
      break;
    }
    case ServiceLaneActionTypes.GetAllNotesFromServerSuccess: {
      state = {
        ...state,
        customerNotes: action.payload.customerNotes,
        appointmentNotes: action.payload.appointmentNotes,
        vehicleNotes: action.payload.vehicleNotes,
      };
      break;
    }
    case ServiceLaneActionTypes.CreateCustomerNotesOnServerSuccess: {
      state = { ...state, customerNotes: action.payload };
      break;
    }
    case ServiceLaneActionTypes.UpdateCustomerNotesOnServerSuccess: {
      state = { ...state, customerNotes: action.payload };
      break;
    }
    case ServiceLaneActionTypes.CreateAppointmentNotesOnServerSuccess: {
      state = { ...state, appointmentNotes: action.payload };
      break;
    }
    case ServiceLaneActionTypes.UpdateAppointmentNotesOnServerSuccess: {
      state = { ...state, appointmentNotes: action.payload };
      break;
    }
    case ServiceLaneActionTypes.CreateVehicleNotesOnServerSuccess: {
      state = { ...state, vehicleNotes: action.payload };
      break;
    }
    case ServiceLaneActionTypes.UpdateVehicleNotesOnServerSuccess: {
      state = { ...state, vehicleNotes: action.payload };
      break;
    }
    case ServiceLaneActionTypes.GetOpenAppointmentsFromServerSuccess: {
      state = {
        ...state,
        openAppointments: appointmentAdapter.addMany(action.payload, {
          ...state.openAppointments,
          status: 'succeeded',
          error: null,
        }),
      };
      break;
    }
    case ServiceLaneActionTypes.GetOpenAppointmentsFromServerError: {
      state = {
        ...state,
        openAppointments: appointmentAdapter.setAll([], {
          ...state.openAppointments,
          status: 'failed',
          error: action.payload,
        }),
      };
      break;
    }
    case ServiceLaneActionTypes.GetUnacknowledgedAppointmentsFromServer: {
      state = {
        ...state,
        unacknowledgedAppointments: {
          ...state.unacknowledgedAppointments,
          status: 'loading',
          error: null,
        },
      };
      break;
    }
    case ServiceLaneActionTypes.GetUnacknowledgedAppointmentsFromServerSuccess: {
      state = {
        ...state,
        unacknowledgedAppointments: appointmentAdapter.addMany(action.payload, {
          ...state.unacknowledgedAppointments,
          status: 'succeeded',
          error: null,
        }),
      };
      break;
    }
    case ServiceLaneActionTypes.GetUnacknowledgedAppointmentsFromServerError: {
      state = {
        ...state,
        unacknowledgedAppointments: appointmentAdapter.setAll([], {
          ...state.unacknowledgedAppointments,
          status: 'failed',
          error: action.payload,
        }),
      };
      break;
    }
    case ServiceLaneActionTypes.UpdateUnacknowledgedAppointmentsInState: {
      state = {
        ...state,
        unacknowledgedAppointments: appointmentAdapter.updateOne(
          action.payload.appointment,
          state.unacknowledgedAppointments
        ),
      };
      break;
    }
    case ServiceLaneActionTypes.CreateAppointmentOnServerSuccess: {
      state = {
        ...state,
        openAppointments: appointmentAdapter.addOne(action.payload, state.openAppointments),
      };
      break;
    }
    case ServiceLaneActionTypes.DeleteAppointmentFromServerSuccess: {
      state = {
        ...state,
        openAppointments: appointmentAdapter.removeOne(action.payload.appointment.id, state.openAppointments),
      };
      break;
    }
    case ServiceLaneActionTypes.GetTodaysAppointmentsFromServerSuccess: {
      state = {
        ...state,
        todaysAppointments: appointmentAdapter.addMany(action.payload.appointments, {
          ...state.todaysAppointments,
          loading: false,
        }),
        todaysWorkOrderAppointments: workOrderAppointmentAdapter.addMany(
          action.payload.workOrderAppointments,
          state.todaysWorkOrderAppointments
        ),
        appointmentFilters: {
          ...state.appointmentFilters,
          myAppointmentsToggle: action.payload.myAppointmentsToggle,
        },
      };
      break;
    }
    case ServiceLaneActionTypes.DeleteTodaysAppointmentFromServerSuccess: {
      state = {
        ...state,
        todaysAppointments: appointmentAdapter.removeOne(action.payload.appointment.id, state.todaysAppointments),
        unacknowledgedAppointments: appointmentAdapter.removeOne(
          action.payload.appointment.id,
          state.unacknowledgedAppointments
        ),
      };
      break;
    }
    case ServiceLaneActionTypes.UpdateTodaysAppointmentsInState: {
      state = {
        ...state,
        todaysAppointments: appointmentAdapter.updateOne(action.payload.appointment, state.todaysAppointments),
      };
      break;
    }
    case ServiceGroupsEntityActionTypes.GetServiceGroupsFromServerSuccess: {
      state = {
        ...state,
        serviceGroups: serviceGroupsAdapter.addMany(action.payload, state.serviceGroups),
      };
      break;
    }
    case QuoteLinesEntityActionTypes.DeleteQuoteLineFromServerSuccess:
    case ServiceLaneActionTypes.RemoveQuoteFromState: {
      state = {
        ...state,
        quoteLines: quoteLinesAdapter.removeOne(action.payload.id, state.quoteLines),
      };
      break;
    }
    case ServiceLaneActionTypes.UpdateApprovedQuoteLineInState: {
      state = {
        ...state,
        approvedQuoteLines: quoteLinesAdapter.updateOne(
          { changes: action.payload.quoteLine, id: action.payload.previousId },
          state.approvedQuoteLines
        ),
      };
      break;
    }
    case ServiceLaneActionTypes.UpdateDeclinedQuoteLineInState: {
      state = {
        ...state,
        declinedQuoteLines: quoteLinesAdapter.updateOne(
          {
            changes: {
              id: action.payload.quoteLine.id,
              embedded: action.payload.quoteLine.embedded,
              sequence: action.payload.quoteLine.sequence,
              transactionTypeId: action.payload.quoteLine.transactionTypeId,
            },
            id: action.payload.previousId,
          },
          state.declinedQuoteLines
        ),
      };
      break;
    }
    case ServiceLaneActionTypes.UpdateDeclinedReasonForQuoteLineInState: {
      state = {
        ...state,
        declinedQuoteLines: quoteLinesAdapter.updateOne(
          {
            changes: {
              declined: action.payload.quoteLine.declined,
            },
            id: action.payload.quoteLine.id,
          },
          state.declinedQuoteLines
        ),
      };
      break;
    }
    case QuoteLinesEntityActionTypes.GetQuoteLinesFromServer: {
      state = {
        ...state,
        quoteLines: quoteLinesAdapter.getInitialState(),
      };
      break;
    }
    case QuoteLinesEntityActionTypes.GetQuoteLinesFromServerSuccess: {
      state = {
        ...state,
        quoteLines: quoteLinesAdapter.addMany(action.payload, state.quoteLines),
      };
      break;
    }
    case ServiceAppointmentDeclinedOperationsEntityActionTypes.GetServiceAppointmentDeclinedOperationsFromServer: {
      state = {
        ...state,
        serviceAppointmentDeclinedOperations: serviceAppointmentDeclinedOperationsAdapter.getInitialState(),
      };
      break;
    }
    // eslint-disable-next-line max-len
    case ServiceAppointmentDeclinedOperationsEntityActionTypes.GetServiceAppointmentDeclinedOperationsFromServerSuccess: {
      state = {
        ...state,
        serviceAppointmentDeclinedOperations: serviceAppointmentDeclinedOperationsAdapter.addMany(
          action.payload,
          state.serviceAppointmentDeclinedOperations
        ),
      };
      break;
    }

    case ServiceLaneActionTypes.GetServiceHistoryFromServer: {
      state = {
        ...state,

        serviceHistory: workOrderAppointmentAdapter.getInitialState(),
      };
      break;
    }
    case ServiceLaneActionTypes.GetServiceHistoryFromServerSuccess: {
      state = {
        ...state,

        serviceHistory: workOrderAppointmentAdapter.addMany(action.payload, state.serviceHistory),
      };
      break;
    }
    case ServiceLaneActionTypes.GetSelectedAppointmentFromServerSuccess: {
      state = { ...state, selectedAppointment: action.payload };
      break;
    }
    case ServiceLaneActionTypes.GetSelectedAppointmentCasesFromServerSuccess: {
      state = {
        ...state,
        selectedAppointmentCases: appointmentCaseAdapter.addMany(action.payload.appointmentCases, {
          ...state.selectedAppointmentCases,
          status: 'succeeded',
          error: null,
        }),
      };
      break;
    }
    case AppointmentCasesEntityActionTypes.GetAppointmentCasesFromServerError: {
      state = {
        ...state,
        selectedAppointmentCases: appointmentCaseAdapter.setAll([], {
          ...state.selectedAppointmentCases,
          status: 'failed',
          error: action.payload,
        }),
      };
      break;
    }
    case ServiceLaneActionTypes.GetVehicleFromServerSuccess:
    case ServiceLaneActionTypes.SetVehicle: {
      state = {
        ...state,
        vehicle: action.payload,
      };
      break;
    }

    case ServiceLaneActionTypes.UpdateSelectedAppointmentCaseOnServerSuccess: {
      state = {
        ...state,
        selectedAppointmentCases: appointmentCaseAdapter.updateOne(
          { id: action.payload.appointmentCase.sequence, changes: action.payload.appointmentCase },
          {
            ...state.selectedAppointmentCases,
          }
        ),
      };
      break;
    }
    case ServiceLaneActionTypes.DeleteSelectedAppointmentCaseOnServerSuccess: {
      state = {
        ...state,
        selectedAppointmentCases: appointmentCaseAdapter.removeOne(action.payload.appointmentCase.sequence, {
          ...state.selectedAppointmentCases,
        }),
      };
      break;
    }

    case ServiceLaneActionTypes.GetOpenAppointmentsFromServer: {
      state = { ...state, openAppointments: initialOpenAppointmentState };
      break;
    }

    case ServiceLaneActionTypes.UpdateSelectedAppointmentOnServerSuccess: {
      state = {
        ...state,
        selectedAppointment: action.payload,
      };
      break;
    }

    case ServiceLaneActionTypes.CreateAppointmentCaseOnServerSuccess: {
      state = {
        ...state,
        selectedAppointmentCases: appointmentCaseAdapter.addOne(action.payload.appointmentCase, {
          ...state.selectedAppointmentCases,
        }),
      };
      break;
    }

    case ServiceLaneActionTypes.UpdateQuoteLineOnServerSuccess: {
      state = {
        ...state,
        quoteLines: quoteLinesAdapter.updateOne({ id: action.payload.id, changes: action.payload }, state.quoteLines),
      };
      break;
    }

    case ServiceLaneActionTypes.UpdateQuoteLineInState: {
      state = {
        ...state,
        quoteLines: quoteLinesAdapter.updateOne({ id: action.payload.id, changes: action.payload }, state.quoteLines),
      };
      break;
    }

    case ServiceLaneActionTypes.ClearCustomerAndVehicleState:
      {
        state = {
          ...state,
          customerAddress: null,
          customerAssociate: null,
          customerNotes: null,
          appointmentNotes: null,
          vehicleNotes: null,
          vehicle: null,
          selectedAppointment: null,
          selectedAppointmentCases: initialSelectedAppointmentCaseState,
          quoteLines: initialQuoteLinesState,
          todaysAppointments: initialtodaysAppointmentState,
          unacknowledgedAppointments: initialUnacknolwedgedAppointmentsState,
          todaysWorkOrderAppointments: initialWorkOrderState,
          approvedQuoteLines: initialQuoteLinesState,
          declinedQuoteLines: initialQuoteLinesState,
        };
      }
      break;

    case ServiceLaneActionTypes.ClearAppointmentState:
      {
        state = {
          ...state,
          selectedAppointment: null,
          selectedAppointmentCases: initialSelectedAppointmentCaseState,
          quoteLines: initialQuoteLinesState,
          todaysAppointments: initialtodaysAppointmentState,
          unacknowledgedAppointments: initialUnacknolwedgedAppointmentsState,
          todaysWorkOrderAppointments: initialWorkOrderState,
          approvedQuoteLines: initialQuoteLinesState,
          declinedQuoteLines: initialQuoteLinesState,
          alternateTransportations: initialAlternateTransportationsState,
        };
      }
      break;

    case ServiceLaneActionTypes.SetVerifyCustomerCreateWOButtonEnabledInState: {
      state = {
        ...state,
        ui: { ...state.ui, verifyCustomerCreateWOEnabled: action.payload },
      };
      break;
    }

    case ServiceLaneActionTypes.SetWorkOrderRequiredFieldsPresentInState: {
      state = {
        ...state,
        ui: { ...state.ui, workOrderRequiredFieldsPresent: action.payload },
      };
      break;
    }

    case ServiceLaneActionTypes.SetUnsavedLineItemInState: {
      state = {
        ...state,
        ui: { ...state.ui, unsavedLineItem: action.payload },
      };
      break;
    }

    case ServiceLaneActionTypes.SetMediaUploadingInState: {
      state = {
        ...state,
        ui: { ...state.ui, mediaUploading: action.payload },
      };
      break;
    }

    case ServiceLaneActionTypes.UpdateAppointmentFiltersInState: {
      state = {
        ...state,
        appointmentFilters: action.payload,
      };
      break;
    }

    case ServiceLaneActionTypes.AddApprovedQuoteLineInState: {
      state = {
        ...state,
        approvedQuoteLines: quoteLinesAdapter.upsertOne(action.payload, state.approvedQuoteLines),
      };
      break;
    }

    case ServiceLaneActionTypes.RemoveApprovedQuoteLineInState: {
      state = {
        ...state,
        approvedQuoteLines: quoteLinesAdapter.removeOne(action.payload.id, state.approvedQuoteLines),
      };
      break;
    }

    case ServiceLaneActionTypes.RemoveDeclinedQuoteLineInState: {
      state = {
        ...state,
        declinedQuoteLines: quoteLinesAdapter.removeOne(action.payload.id, state.declinedQuoteLines),
      };
      break;
    }

    case ServiceLaneActionTypes.AddDeclinedQuoteLineInState: {
      state = {
        ...state,
        declinedQuoteLines: quoteLinesAdapter.upsertOne(action.payload, state.declinedQuoteLines),
      };
      break;
    }

    case ServiceLaneActionTypes.GetAlternateTransportationsFromServerSuccess: {
      state = {
        ...state,
        alternateTransportations: alternateTransportationsAdapter.setAll(
          action.payload,
          state.alternateTransportations
        ),
      };
      break;
    }

    case ServiceLaneActionTypes.UpdateAlternateTransportationOnServerSuccess: {
      state = {
        ...state,
        alternateTransportations: alternateTransportationsAdapter.upsertOne(
          action.payload,
          state.alternateTransportations
        ),
      };
      break;
    }

    case ServiceLaneActionTypes.CreateAlternateTransportationOnServerSuccess: {
      state = {
        ...state,
        alternateTransportations: alternateTransportationsAdapter.addOne(action.payload, {
          ...state.alternateTransportations,
        }),
      };
      break;
    }

    case ServiceLaneActionTypes.DeleteAlternateTransportationOnServer: {
      state = {
        ...state,
        alternateTransportations: alternateTransportationsAdapter.removeOne(
          action.payload.id,
          state.alternateTransportations
        ),
      };
      break;
    }
  }
  return state;
}
